/**
 * OverviewFrame: the shared shell of both overview pages. A `PageHeader` (h1) with an "As of {time}" line and a
 * Refresh icon button (ghost, labelled) that refetches every overview query, optional page `filters`, then a Carbon `Grid` with the page's widgets as children
 * (`OverviewRow` for each row). With `isEmpty` (no widget visible to this user) it shows an EmptyState instead.
 */
import { Column, Grid, IconButton } from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import { useTranslation } from '@pos/i18n';
import { useIsFetching } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import '../ResourcePage.scss';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { useFormat } from '../orders/model';
import { overviewKey, useRefreshOverview } from './hooks';
import './overview.scss';

export function OverviewFrame({
  title,
  description,
  isEmpty,
  filters,
  children,
}: {
  title: string;
  description?: string;
  isEmpty: boolean;
  /** Filters for the whole page (a period, a store), between the header and the widgets. */
  filters?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const refresh = useRefreshOverview();
  const fetching = useIsFetching({ queryKey: overviewKey });
  const [asOf, setAsOf] = useState(() => new Date());
  const wasFetching = useRef(false);
  useEffect(() => {
    if (wasFetching.current && fetching === 0) setAsOf(new Date());
    wasFetching.current = fetching > 0;
  }, [fetching]);

  return (
    <section className="pos-admin-page">
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            <span className="pos-overview__asof">{t('admin.overview.asOf', { time: format.time(asOf) })}</span>
            <IconButton
              kind="ghost"
              label={t('admin.overview.refresh')}
              align="bottom-end"
              onClick={() => void refresh()}
              disabled={fetching > 0}
            >
              <Renew />
            </IconButton>
          </>
        }
      />
      {!isEmpty && filters && <div className="pos-overview__filters">{filters}</div>}
      {isEmpty ? (
        <EmptyState heading={t('admin.overview.noAccessTitle')} description={t('admin.overview.noAccess')} />
      ) : (
        <Grid fullWidth condensed className="pos-overview__grid">
          {children}
        </Grid>
      )}
    </section>
  );
}

/** One widget in the grid: full width on small screens, `span` of 16 columns from the large breakpoint. */
export function OverviewColumn({ span = 8, children }: { span?: 4 | 8 | 16; children: ReactNode }) {
  return (
    <Column sm={4} md={8} lg={span} className="pos-overview__column">
      {children}
    </Column>
  );
}
